import React, { useEffect, useMemo, useState } from "react";
import { Table as ChakraTable, Tbody } from "@chakra-ui/react";
import {
  ColumnDef,
  getCoreRowModel,
  RowSelectionOptions,
  useReactTable,
} from "@tanstack/react-table";
import TableFooter from "./TableFooter";
import TableHead from "./TableHead";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  UniqueIdentifier,
  DragEndEvent,
  useSensors,
  useSensor,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  DndContext,
  closestCenter,
} from "@dnd-kit/core";
import DraggableRow, { DefaultTableData } from "./DraggableRow";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { InfiniteData } from "@tanstack/react-query";
import { TableNameE } from "shared/api/types/common";
import {
  ListQueryRequestType,
  QueryOptionsT,
  InfiniteQueryResult,
} from "shared/api/types/typedQueries";
import { FilterField } from "shared/types/filters";
import useGetSortingMethods from "./hooks/useGetSortingMethods";
import TableContainer from "./TableContainer";

interface Props<QueryData extends ListQueryRequestType<DefaultTableData>> {
  columns: ColumnDef<ArrayElement<QueryData["response"]["data"]>>[];
  query: (
    params?: Pick<QueryData, "pathParams" | "params">,
    options?: QueryOptionsT,
  ) => InfiniteQueryResult<InfiniteData<QueryData["response"]>>;
  filterFields?: FilterField[];
  tableName: TableNameE;
  hidePagination?: boolean;
  onChangeOrder: (id: string | number, newOrder: number) => void;
  onRowSelectionChange?: RowSelectionOptions<
    ArrayElement<QueryData["response"]["data"]>
  >["onRowSelectionChange"];
  rowSelection?: Record<string, boolean>;
  disableSearch?: boolean;

}

const DragableTable = <
  QueryData extends ListQueryRequestType<DefaultTableData>,
>({
  query,
  columns,
  filterFields,
  tableName,
  hidePagination,
  onChangeOrder,
  onRowSelectionChange,
  rowSelection = {},
  disableSearch,

}: Props<QueryData>) => {
  type TData = ArrayElement<QueryData["response"]["data"]>;
  const tableSortingMethods = useGetSortingMethods<QueryData>({
    filterFields,
    query,
  });
  const { data, order, sortBy, changeOrder, changeSortBy } =
    tableSortingMethods;
  const [renderedData, setRenderedData] = useState<TData[]>([]);
  const [columnsToRender, setColumnsToRender] = useState(columns);

  useEffect(() => {
    if (data) {
      setRenderedData(data);
    }
  }, [data]);

  const dataIds = React.useMemo<UniqueIdentifier[]>(
    () => renderedData?.map(({ id }) => id) || [],
    [renderedData],
  );

  const table = useReactTable({
    data: renderedData,
    columns: columnsToRender,
    enableRowSelection: true,
    state: {
      rowSelection,
    },
    onRowSelectionChange,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id as string,
    debugTable: false,
    debugHeaders: false,
    debugColumns: false,
  });

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (active && over && active.id !== over.id) {
      const oldIndex = dataIds.indexOf(active.id);
      const newIndex = dataIds.indexOf(over.id);
      setRenderedData((data) => arrayMove(data, oldIndex, newIndex));

      const destinationRow = renderedData.find((row) => row.id === over.id);
      const newOrder = (destinationRow as any)?.order ?? newIndex;
      onChangeOrder(active.id, newOrder - 1);
    }
  }

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {}),
  );

  const selectedRowIds = useMemo(
    () => Object.keys(rowSelection),
    [rowSelection],
  );

  return (
    <TableContainer
      columns={columns}
      setColumnsToRender={setColumnsToRender}
      tableName={tableName}
      filterFields={filterFields}
      hidePagination={hidePagination}
      disableSearch={disableSearch}
      {...tableSortingMethods}
    >
      <DndContext
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={handleDragEnd}
        sensors={sensors}
      >
        <div className="p-2">
          <ChakraTable
            size="sm"
            w="100%"
            // style={{ tableLayout: 'fixed' }}
            minWidth="max-content"
            width="-webkit-fill-available"
          >
            <TableHead<TData>
              table={table}
              setOrder={changeOrder}
              setSortBy={changeSortBy}
              order={order}
              sortBy={sortBy}
            />
            <Tbody>
              <SortableContext
                items={dataIds}
                strategy={verticalListSortingStrategy}
              >
                {table.getRowModel().rows.map((row) => (
                  <DraggableRow
                    draggable
                    key={row.id}
                    row={row}
                    selected={selectedRowIds.includes(
                      row.original.id.toString(),
                    )}
                  />
                ))}
              </SortableContext>
            </Tbody>
            <TableFooter<TData> table={table} />
          </ChakraTable>
        </div>
      </DndContext>
    </TableContainer>
  );
};

export default DragableTable;
